import React, { useEffect, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  RefreshCw,
  Shield,
  ShieldAlert,
  Siren,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { toast } from 'sonner';

import DataTable from '@/components/DataTable';
import EmptyState from '@/components/EmptyState';
import ErrorBoundary from '@/components/ErrorBoundary';
import FeatureGate from '@/components/FeatureGate';
import LoadingSkeleton from '@/components/LoadingSkeleton';
import RiskGauge from '@/components/RiskGauge';
import SeverityBadge from '@/components/SeverityBadge';
import StatusBadge from '@/components/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import api from '@/lib/api';

const RANGE_OPTIONS = [
  { key: '7d', label: '7 days', days: 7 },
  { key: '30d', label: '30 days', days: 30 },
  { key: '90d', label: '90 days', days: 90 },
];

const FACTOR_LABELS = {
  file_access: 'File Access',
  web_diversity: 'Web Diversity',
  new_domains: 'New Domains',
  ai_usage: 'AI Usage',
  usb_activity: 'USB Activity',
  off_hours: 'Off Hours',
};

const borderMap = {
  critical: 'border-red-500/50',
  high: 'border-orange-500/50',
  medium: 'border-yellow-500/40',
  low: 'border-emerald-500/40',
};

const tooltipStyle = {
  backgroundColor: '#1e293b',
  border: '1px solid #334155',
  borderRadius: '12px',
  color: '#e2e8f0',
};

function getDateWindow(rangeKey) {
  const range = RANGE_OPTIONS.find((option) => option.key === rangeKey) || RANGE_OPTIONS[1];
  const end = new Date();
  end.setHours(23, 59, 59, 999);

  const start = new Date(end);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (range.days - 1));

  return {
    days: range.days,
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

function formatDayLabel(value) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(new Date(value));
}

function formatTimestamp(value) {
  if (!value) return 'Unknown';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatAlertType(value) {
  return String(value || 'unknown').replace(/_/g, ' ');
}

function topFactor(factors = {}) {
  const entries = Object.entries(factors);

  if (!entries.length) {
    return { key: 'baseline', label: 'Stable baseline', score: 0 };
  }

  const [factorKey, factorValue] = entries.sort(
    (first, second) => (second[1]?.score || 0) - (first[1]?.score || 0)
  )[0];

  return {
    key: factorKey,
    label: FACTOR_LABELS[factorKey] || factorKey.replace(/_/g, ' '),
    score: factorValue?.score || 0,
  };
}

function buildDistribution(scores = []) {
  const counts = { critical: 0, high: 0, medium: 0, low: 0 };

  scores.forEach((score) => {
    const level = String(score.risk_level || 'low').toLowerCase();
    if (Object.prototype.hasOwnProperty.call(counts, level)) {
      counts[level] += 1;
    }
  });

  return [
    { name: 'Critical', count: counts.critical, fill: '#f43f5e' },
    { name: 'High', count: counts.high, fill: '#fb923c' },
    { name: 'Medium', count: counts.medium, fill: '#facc15' },
    { name: 'Low', count: counts.low, fill: '#22c55e' },
  ];
}

function buildTrend(historyByEmployee = {}, days = 30) {
  const trend = Array.from({ length: days }).map((_, index) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - (days - index - 1));
    const key = date.toISOString().slice(0, 10);

    return {
      date: key,
      label: formatDayLabel(key),
      score: 0,
      samples: 0,
    };
  });

  const pointMap = trend.reduce((accumulator, point) => {
    accumulator[point.date] = point;
    return accumulator;
  }, {});

  Object.values(historyByEmployee).forEach((history) => {
    history.forEach((item) => {
      const dateKey = String(item.calculated_at || '').slice(0, 10);
      if (!pointMap[dateKey]) return;
      pointMap[dateKey].score += item.overall_risk || 0;
      pointMap[dateKey].samples += 1;
    });
  });

  return trend.map((point) => ({
    ...point,
    score: point.samples ? Number((point.score / point.samples).toFixed(1)) : 0,
  }));
}

function buildRadarData(scores = [], selectedEmployee = null) {
  const keys = Object.keys(FACTOR_LABELS);

  if (selectedEmployee) {
    return keys.map((key) => ({
      factor: FACTOR_LABELS[key],
      value: selectedEmployee.factors?.[key]?.score || 0,
    }));
  }

  return keys.map((key) => {
    const total = scores.reduce((sum, employee) => sum + (employee.factors?.[key]?.score || 0), 0);
    return {
      factor: FACTOR_LABELS[key],
      value: scores.length ? Number((total / scores.length).toFixed(1)) : 0,
    };
  });
}

function RiskDashboardContent() {
  const [range, setRange] = useState('30d');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [data, setData] = useState({
    scores: [],
    employeesById: {},
    alerts: [],
    historyByEmployee: {},
  });

  const loadRiskDashboard = async ({ silent = false } = {}) => {
    const { startDate } = getDateWindow(range);

    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    const [scoresResult, employeesResult, alertsResult] = await Promise.allSettled([
      api.get('/analytics/risk-scores'),
      api.get('/employees', { params: { page: 1, page_size: 200 } }),
      api.get('/alerts', { params: { page: 1, page_size: 50 } }),
    ]);

    const scores = scoresResult.status === 'fulfilled' ? scoresResult.value.data?.items || [] : [];
    const employees =
      employeesResult.status === 'fulfilled' ? employeesResult.value.data?.items || [] : [];
    const alerts = alertsResult.status === 'fulfilled' ? alertsResult.value.data?.items || [] : [];

    const historyResults = await Promise.allSettled(
      scores.map((employee) => api.get(`/analytics/risk-scores/${employee.employee_id}`))
    );

    const historyByEmployee = historyResults.reduce((accumulator, result, index) => {
      if (result.status === 'fulfilled') {
        accumulator[scores[index].employee_id] = result.value.data?.items || [];
      } else {
        accumulator[scores[index].employee_id] = [];
      }
      return accumulator;
    }, {});

    const failures = [
      scoresResult,
      employeesResult,
      alertsResult,
      ...historyResults,
    ].filter((result) => result.status === 'rejected');

    if (failures.length) {
      toast.error('Some risk analytics panels could not be loaded.');
    }

    const employeesById = employees.reduce((accumulator, employee) => {
      accumulator[employee.id] = employee;
      return accumulator;
    }, {});

    const filteredAlerts = alerts.filter((alert) => new Date(alert.ts) >= new Date(startDate));

    const sortedScores = [...scores].sort(
      (first, second) => (second.overall_risk || 0) - (first.overall_risk || 0)
    );

    setSelectedEmployeeId((current) => {
      if (current && sortedScores.some((employee) => employee.employee_id === current)) {
        return current;
      }
      return sortedScores[0]?.employee_id || null;
    });

    setData({
      scores: sortedScores,
      employeesById,
      alerts: filteredAlerts,
      historyByEmployee,
    });

    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    loadRiskDashboard();
  }, [range]);

  useEffect(() => {
    if (!autoRefresh) return undefined;

    const interval = window.setInterval(() => {
      loadRiskDashboard({ silent: true });
    }, 60000);

    return () => window.clearInterval(interval);
  }, [autoRefresh, range]);

  const selectedEmployee =
    data.scores.find((employee) => employee.employee_id === selectedEmployeeId) || null;
  const organizationRisk = data.scores.length
    ? data.scores.reduce((sum, item) => sum + (item.overall_risk || 0), 0) / data.scores.length
    : 0;
  const distributionData = buildDistribution(data.scores);
  const trendData = buildTrend(data.historyByEmployee, getDateWindow(range).days);
  const radarData = buildRadarData(data.scores, selectedEmployee);
  const highSeverityAlerts = data.alerts
    .filter((alert) => ['critical', 'high'].includes(String(alert.severity || '').toLowerCase()))
    .slice(0, 10);

  return (
    <div className="dashboard-mesh security-grid min-h-screen rounded-[28px] bg-slate-900 p-1 text-white">
      <div className="rounded-[24px] border border-slate-800 bg-slate-900/85 p-6 backdrop-blur">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <p className="text-sm uppercase tracking-[0.35em] text-slate-500">Security Operations</p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <h1 className="text-4xl font-semibold tracking-tight text-white">Risk Dashboard</h1>
              <Badge className="border-amber-400/40 bg-amber-500/10 text-amber-200">
                Enterprise
              </Badge>
            </div>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">
              Behavioral anomaly detection, risk posture, and current escalation pressure for live demo
              walkthroughs.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="inline-flex rounded-xl border border-slate-700 bg-slate-800/80 p-1">
              {RANGE_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={`rounded-lg px-4 py-2 text-sm transition ${
                    range === option.key
                      ? 'bg-emerald-500 text-slate-950 shadow-lg shadow-emerald-500/20'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                  onClick={() => setRange(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 rounded-xl border border-slate-700 bg-slate-800 px-4 py-2">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-slate-500">Auto refresh</p>
                <p className="text-sm text-slate-300">Every 60 seconds</p>
              </div>
              <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} />
            </div>

            <Button
              className="border border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"
              onClick={() => loadRiskDashboard({ silent: true })}
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          <Card className="border-slate-700 bg-slate-800 shadow-lg">
            <CardHeader className="pb-0">
              <CardTitle className="flex items-center justify-between gap-3 text-white">
                <span className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-emerald-400" />
                  Organization Risk
                </span>
                <SeverityBadge
                  severity={
                    organizationRisk >= 80
                      ? 'critical'
                      : organizationRisk >= 60
                        ? 'high'
                        : organizationRisk >= 40
                          ? 'medium'
                          : 'low'
                  }
                />
              </CardTitle>
            </CardHeader>
            <CardContent className="flex h-full flex-col items-center justify-center pt-6">
              {loading && !data.scores.length ? (
                <LoadingSkeleton type="chart" />
              ) : (
                <>
                  <RiskGauge score={organizationRisk} size="lg" />
                  <div className="mt-4 text-center">
                    <p className="text-sm text-slate-400">Monitored employees</p>
                    <p className="mt-1 text-2xl font-semibold text-white">{data.scores.length}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-700 bg-slate-800 shadow-lg">
            <CardHeader className="pb-0">
              <CardTitle className="flex items-center gap-2 text-white">
                <ShieldAlert className="h-5 w-5 text-orange-400" />
                Risk Distribution
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {loading && !data.scores.length ? (
                <LoadingSkeleton type="chart" />
              ) : distributionData.some((item) => item.count > 0) ? (
                <div className="h-[260px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={distributionData} layout="vertical" margin={{ left: 10, right: 10 }}>
                      <CartesianGrid stroke="#334155" horizontal={false} />
                      <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        tick={{ fill: '#94a3b8', fontSize: 12 }}
                        width={72}
                      />
                      <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(51, 65, 85, 0.25)' }} />
                      <Bar dataKey="count" radius={[0, 10, 10, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <EmptyState
                  icon={<ShieldAlert className="h-8 w-8" />}
                  title="No scored employees"
                  description="Distribution becomes available once the scoring worker produces risk records."
                />
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-700 bg-slate-800 shadow-lg">
            <CardHeader className="pb-0">
              <CardTitle className="flex items-center gap-2 text-white">
                <Activity className="h-5 w-5 text-blue-400" />
                Risk Trend
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {loading && !data.scores.length ? (
                <LoadingSkeleton type="chart" />
              ) : trendData.some((point) => point.score > 0) ? (
                <div className="h-[260px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData}>
                      <defs>
                        <linearGradient id="riskAreaFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.45} />
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                      <YAxis domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Area
                        type="monotone"
                        dataKey="score"
                        stroke="#10b981"
                        fill="url(#riskAreaFill)"
                        strokeWidth={2.5}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <EmptyState
                  icon={<Activity className="h-8 w-8" />}
                  title="No historical trend"
                  description="Per-employee risk history has not accumulated enough points yet."
                />
              )}
            </CardContent>
          </Card>
        </div>

        <div className="mt-6">
          <FeatureGate feature="risk_scoring">
            <Card className="border-slate-700 bg-slate-800 shadow-lg">
              <CardHeader className="pb-0">
                <CardTitle className="text-white">Employee Risk Heat Map</CardTitle>
                <p className="text-sm text-slate-500">
                  Highest-risk employees are sorted to the front. Select one to inspect factor breakdown.
                </p>
              </CardHeader>
              <CardContent className="pt-6">
                {loading && !data.scores.length ? (
                  <LoadingSkeleton type="card" />
                ) : data.scores.length ? (
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                    {data.scores.map((employee) => {
                      const employeeMeta = data.employeesById[employee.employee_id];
                      const factor = topFactor(employee.factors);
                      const selected = employee.employee_id === selectedEmployeeId;

                      return (
                        <button
                          key={employee.employee_id}
                          type="button"
                          className={`rounded-2xl border bg-slate-900/80 p-4 text-left transition hover:-translate-y-0.5 hover:bg-slate-900 ${borderMap[employee.risk_level] || borderMap.low} ${selected ? 'ring-2 ring-emerald-400/60' : ''}`}
                          onClick={() => setSelectedEmployeeId(employee.employee_id)}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="truncate font-medium text-white">{employee.employee_name}</p>
                              <p className="mt-1 truncate text-xs text-slate-500">
                                {employeeMeta?.team_name || 'Unassigned team'}
                              </p>
                            </div>
                            <SeverityBadge
                              severity={
                                employee.risk_level === 'critical'
                                  ? 'critical'
                                  : employee.risk_level === 'high'
                                    ? 'high'
                                    : employee.risk_level === 'medium'
                                      ? 'medium'
                                      : 'low'
                              }
                            />
                          </div>
                          <div className="mt-4 flex items-center justify-between gap-3">
                            <RiskGauge score={employee.overall_risk} size="sm" />
                            <div className="min-w-0 flex-1">
                              <p className="text-xs uppercase tracking-[0.25em] text-slate-500">
                                Top factor
                              </p>
                              <p className="mt-1 text-sm text-slate-200">{factor.label}</p>
                              <p className="mt-1 text-xs text-slate-500">
                                Score {Math.round(factor.score)}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyState
                    icon={<Shield className="h-8 w-8" />}
                    title="No risk map available"
                    description="The worker has not produced any employee risk snapshots yet."
                  />
                )}
              </CardContent>
            </Card>
          </FeatureGate>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.5fr),minmax(0,1fr)]">
          <FeatureGate feature="risk_scoring">
            <Card className="border-slate-700 bg-slate-800 shadow-lg">
              <CardHeader className="pb-0">
                <CardTitle className="flex items-center justify-between gap-3 text-white">
                  <span>Risk Factor Breakdown</span>
                  <span className="text-sm font-normal text-slate-500">
                    {selectedEmployee ? selectedEmployee.employee_name : 'Organization average'}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {loading && !radarData.length ? (
                  <LoadingSkeleton type="chart" />
                ) : (
                  <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr),260px] xl:items-center">
                    <div className="h-[360px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={radarData}>
                          <PolarGrid stroke="#334155" />
                          <PolarAngleAxis dataKey="factor" tick={{ fill: '#cbd5e1', fontSize: 12 }} />
                          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 10 }} />
                          <Tooltip contentStyle={tooltipStyle} />
                          <Radar
                            name="Risk factor"
                            dataKey="value"
                            stroke="#8b5cf6"
                            fill="#8b5cf6"
                            fillOpacity={0.3}
                            strokeWidth={2}
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-3">
                      {radarData.map((factor) => (
                        <div key={factor.factor} className="rounded-xl border border-slate-700 bg-slate-900/70 p-3">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-sm text-slate-300">{factor.factor}</span>
                            <span className="text-sm font-medium text-white">{Math.round(factor.value)}</span>
                          </div>
                          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-700">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-purple-500 via-blue-500 to-emerald-400"
                              style={{ width: `${Math.max(4, factor.value)}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </FeatureGate>

          <Card className="border-slate-700 bg-slate-800 shadow-lg">
            <CardHeader className="pb-0">
              <CardTitle className="flex items-center gap-2 text-white">
                <Siren className="h-5 w-5 text-rose-400" />
                Recent High-Severity Alerts
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <DataTable
                loading={loading && !highSeverityAlerts.length}
                data={highSeverityAlerts}
                emptyMessage="No high-severity alerts landed in this time window."
                columns={[
                  {
                    key: 'severity',
                    label: 'Severity',
                    render: (value) => <SeverityBadge severity={value} />,
                  },
                  {
                    key: 'employee_name',
                    label: 'Employee',
                    render: (value) => value || 'System',
                  },
                  {
                    key: 'type',
                    label: 'Alert',
                    render: (value) => formatAlertType(value),
                  },
                  {
                    key: 'status',
                    label: 'Status',
                    render: (value) => <StatusBadge status={value} />,
                  },
                  {
                    key: 'ts',
                    label: 'Timestamp',
                    render: (value) => formatTimestamp(value),
                  },
                ]}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default function RiskDashboard() {
  return (
    <ErrorBoundary>
      <RiskDashboardContent />
    </ErrorBoundary>
  );
}
